import React, { useRef } from 'react';
import { TabunganSiswa, Siswa } from '../../types/index.js';
import { X, Printer, Download, PiggyBank } from 'lucide-react';

interface TabunganStatementModalProps {
  siswa: Siswa;
  mutasiList: TabunganSiswa[];
  onClose: () => void;
}

export const TabunganStatementModal: React.FC<TabunganStatementModalProps> = ({
  siswa,
  mutasiList,
  onClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (printRef.current) {
      const originalContents = document.body.innerHTML;
      const printContents = printRef.current.innerHTML;
      
      // Setup a print-specific styling wrapper
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>E-Statement - ${siswa.nama}</title>
              <style>
                body { font-family: 'Courier New', Courier, monospace; padding: 20px; font-size: 12px; }
                .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
                .school-name { font-size: 18px; font-weight: bold; }
                .statement-title { font-size: 16px; margin: 10px 0; text-transform: uppercase; }
                .info-table { width: 100%; margin-bottom: 20px; text-align: left; }
                .info-table td { padding: 4px; }
                .mutasi-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                .mutasi-table th, .mutasi-table td { border: 1px solid #000; padding: 8px; text-align: right; }
                .mutasi-table th { background-color: #f0f0f0; text-align: center; font-weight: bold; }
                .mutasi-table td:nth-child(1), .mutasi-table td:nth-child(2), .mutasi-table td:nth-child(6) { text-align: left; }
                .text-right { text-align: right; }
                .footer { margin-top: 40px; display: flex; justify-content: space-between; }
                .signature-box { text-align: center; width: 200px; }
              </style>
            </head>
            <body>
              ${printContents}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  const handleSendWA = () => {
    alert('Fitur pengiriman PDF via WhatsApp Gateway akan terintegrasi dengan modul komunikasi API.');
  };

  // Sort chronologically for statement (oldest to newest)
  const sortedMutasi = [...mutasiList].sort(
    (a, b) => new Date(a.tanggal).getTime() - new Date(b.tanggal).getTime()
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* Header Actions */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl">
          <div className="flex items-center gap-2 text-slate-800">
            <PiggyBank className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold">E-Statement Buku Tabungan</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSendWA}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-bold rounded-lg transition"
            >
              <Download className="w-4 h-4" />
              Kirim via WA
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-lg transition shadow-sm"
            >
              <Printer className="w-4 h-4" />
              Print Passbook
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-200 text-slate-500 rounded-lg ml-2 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Printable Area */}
        <div className="p-6 overflow-y-auto bg-slate-100 custom-scrollbar flex-1">
          <div 
            ref={printRef} 
            className="bg-white p-10 max-w-3xl mx-auto shadow-sm" 
            style={{ minHeight: '800px' }}
          >
            {/* Header Document */}
            <div className="header">
              <div className="school-name">SD MUHAMMADIYAH TERPADU PONOROGO</div>
              <div>Jl. Parikesit No.24, Kepatihan, Kec. Ponorogo, Kab. Ponorogo, Jawa Timur 63416</div>
              <h2 className="statement-title">E-Statement / Rekening Koran Tabungan</h2>
            </div>

            {/* Nasabah Info */}
            <table className="info-table">
              <tbody>
                <tr>
                  <td style={{ width: '150px', fontWeight: 'bold' }}>Nama Nasabah</td>
                  <td style={{ width: '20px' }}>:</td>
                  <td>{siswa.nama}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Nomor Induk (NIS)</td>
                  <td>:</td>
                  <td>{siswa.nis}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Kelas & Angkatan</td>
                  <td>:</td>
                  <td>{siswa.kelasNama} / {siswa.thnAngkatan}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Periode Cetak</td>
                  <td>:</td>
                  <td>{new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                </tr>
              </tbody>
            </table>

            {/* Mutasi Table */}
            <table className="mutasi-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>No</th>
                  <th style={{ width: '120px' }}>Tanggal</th>
                  <th style={{ width: '150px' }}>Keterangan</th>
                  <th>Debit (Keluar)</th>
                  <th>Kredit (Masuk)</th>
                  <th>Saldo Akhir</th>
                </tr>
              </thead>
              <tbody>
                {sortedMutasi.length > 0 ? (
                  sortedMutasi.map((m, idx) => (
                    <tr key={m.id}>
                      <td>{idx + 1}</td>
                      <td>{new Date(m.tanggal).toLocaleDateString('id-ID')}</td>
                      <td>{m.keterangan || (m.jenis === 'SETOR' || m.tipe === 'SETOR' ? 'Setoran' : 'Penarikan')}</td>
                      <td>{(m.jenis === 'TARIK' || m.jenis === 'BAYAR_TAGIHAN' || m.tipe === 'TARIK') ? `Rp ${m.nominal.toLocaleString('id-ID')}` : '-'}</td>
                      <td>{(m.jenis === 'SETOR' || m.tipe === 'SETOR') ? `Rp ${m.nominal.toLocaleString('id-ID')}` : '-'}</td>
                      <td style={{ fontWeight: 'bold' }}>Rp {m.saldoSesudah.toLocaleString('id-ID')}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                      Tidak ada histori transaksi
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Footer Signatures */}
            <div className="footer">
              <div className="signature-box">
                <p>Mengetahui,</p>
                <p style={{ marginTop: '60px', textDecoration: 'underline', fontWeight: 'bold' }}>Bendahara / Kasir</p>
              </div>
              <div className="signature-box">
                <p>Nasabah,</p>
                <p style={{ marginTop: '60px', textDecoration: 'underline', fontWeight: 'bold' }}>{siswa.nama}</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
